// AI Receptionist page content. Based on the CLI Secure AI receptionist page (same platform), in VOIP2 branding.
const M = '/media/ai';

export const AI_RECEPTIONIST = {
  hero: {
    title: 'A receptionist that',
    highlight: 'never misses',
    after: ' a call',
    sub: 'An AI voice agent that answers every call, understands what the caller needs, books appointments and puts people through to the right person, day and night.',
    bullets: ['24/7 call coverage', 'Natural conversations', 'Books appointments', 'Multilingual'],
  },

  trust: [
    { value: '24/7', label: 'every call answered' },
    { value: 'Natural', label: 'speech, no keypad menus' },
    { value: 'Calendar', label: 'booking with Google & Microsoft 365' },
    { value: 'Handover', label: 'to your team with full context' },
  ],

  intro: {
    title: 'AI that understands what callers need',
    paragraphs: [
      'Every missed call is a missed opportunity. The VOIP2 AI Receptionist understands not just what callers say but what they want, answers their questions and completes tasks like booking appointments, without passing on calls it can handle itself.',
      'Callers get an immediate answer, a natural conversation instead of a keypad menu, and a quick handover to a person when they need one.',
    ],
    bullets: ['Answers in your business name', 'Learns from your own documents', 'Transfers to any extension', 'Every call logged and transcribed'],
  },

  platform: {
    title: 'Set it up yourself in minutes',
    sub: 'Choose a voice, write the greeting, add your documents and review every conversation from the VOIP2 portal. Click a screen for a closer look.',
    screens: [
      { img: `${M}/screen-setup.webp`, title: 'Voice, greeting and knowledge', body: 'Pick a voice and languages, write the greeting and upload the files and website it should learn from.' },
      { img: `${M}/screen-calllog.webp`, title: 'Call log and transcripts', body: 'See each caller’s intent and outcome, read the transcript and play the recording.' },
    ],
  },

  features: {
    title: 'What makes it different',
    sub: 'Voice AI that recognises intent and acts on every call.',
    items: [
      { icon: 'Brain', title: 'Understands intent', body: 'Recognises what the caller wants and responds to it, rather than matching keywords.' },
      { icon: 'Languages', title: 'Speaks their language', body: 'Detects the caller’s language and replies in it, without multilingual staff.' },
      { icon: 'CalendarDays', title: 'Books appointments', body: 'Checks Google Calendar or Microsoft 365 availability live, books the slot and sends an SMS confirmation.' },
      { icon: 'Route', title: 'Routes intelligently', body: 'Handles routine enquiries itself and transfers complex ones to the right person with the context.' },
      { icon: 'Mic', title: 'Your brand voice', body: 'Choose the voice and tone and set the rules for when to hand over.' },
      { icon: 'ChartColumn', title: 'Analytics and training', body: 'See why customers call and which questions went unanswered, then add the answers.' },
    ],
  },

  transcript: {
    title: 'Hear how a call goes',
    sub: 'A typical booking call: no menus, no hold music. The caller says what they need and the receptionist does it.',
    points: ['Greets in your business name', 'Checks your calendar live', 'Confirms by SMS', 'Logs the call with intent and outcome'],
    lines: [
      { who: 'ai', text: 'Thanks for calling Northgate Dental, how can I help?' },
      { who: 'caller', text: 'Hi, I’d like to book a check-up, please.' },
      { who: 'ai', text: 'Of course. Are you an existing patient?' },
      { who: 'caller', text: 'Yes, it’s Sam Carter.' },
      { who: 'ai', text: 'Thanks, Sam. I have tomorrow at 10:00 or Thursday at 14:30. Which suits you?' },
      { who: 'caller', text: 'Tomorrow at ten is great.' },
      { who: 'ai', text: 'You’re booked for tomorrow at 10:00. I’ll text you a confirmation now. Anything else?' },
      { who: 'caller', text: 'No, that’s all. Thank you!' },
    ],
  },

  compare: {
    title: 'How it compares',
    head: ['', 'Auto-attendant', 'Traditional IVR', 'VOIP2 AI Receptionist'],
    rows: [
      ['Natural conversation', 'No', 'Limited', 'Yes'],
      ['Understands intent', 'No', 'Keyword-based', 'Yes'],
      ['Books appointments', 'No', 'No', 'Yes'],
      ['Resolves the call', 'Transfers only', 'Limited', 'Most routine calls'],
      ['Improves over time', 'No', 'No', 'Yes'],
      ['Multilingual', 'No', 'No', 'Yes'],
    ],
  },

  industries: {
    title: 'Built for businesses that live on the phone',
    items: [
      { icon: 'Briefcase', title: 'Professional services', body: 'Law firms, accountants and agencies qualify enquiries and book consultations.', points: ['Lead qualification', 'Consultation booking', 'Client intake'] },
      { icon: 'Stethoscope', title: 'Healthcare and dental', body: 'Clinics book appointments and cover after-hours calls, cutting no-shows.', points: ['Appointment booking', 'Reminders', 'Patient enquiries'] },
      { icon: 'ShoppingBag', title: 'Retail and e-commerce', body: 'Answer order, delivery and returns questions around the clock.', points: ['Order status', 'Product questions', 'Returns'] },
      { icon: 'Hotel', title: 'Hospitality and travel', body: 'Hotels and agencies take bookings and handle changes any time of day.', points: ['Reservations', 'Changes and cancellations', 'Guest questions'] },
      { icon: 'House', title: 'Property', body: 'Agents book viewings and capture buyer and tenant details before follow-up.', points: ['Viewing bookings', 'Property enquiries', 'Tenant support'] },
      { icon: 'Building2', title: 'Small offices', body: 'A professional front desk without hiring one, and no more calls to voicemail.', points: ['Call answering', 'Message taking', 'Call transfer'] },
    ],
  },

  steps: [
    { verb: 'Book a demo', body: 'See it handle calls for a business like yours.' },
    { verb: 'Set it up', body: 'Choose a voice, add your knowledge and set the transfer rules.' },
    { verb: 'Go live', body: 'Point a VOIP2 number at it and review every call in the portal.' },
  ],

  faqs: [
    { q: 'What is an AI receptionist?', a: 'A voice AI that answers your business calls like a human receptionist: it greets callers, understands what they need, answers questions, books appointments and transfers to the right person, 24/7.' },
    { q: 'Will callers know they are talking to AI?', a: 'Conversations are natural, and you choose the voice, tone and greeting. Whenever a caller needs a person, the call is handed to your team with the context so they do not have to repeat themselves.' },
    { q: 'Can it book appointments?', a: 'Yes. It checks Google Calendar or Microsoft 365 availability in real time, books the slot and sends an SMS confirmation.' },
    { q: 'Which languages does it speak?', a: 'It detects the caller’s language and replies in it across many major languages, so you can serve international customers without multilingual staff.' },
    { q: 'How does it learn about my business?', a: 'Upload your FAQs, price lists and documents, or point it at your website. The call log shows which questions it could not answer so you can add them.' },
    { q: 'Which numbers can it answer?', a: 'Any VOIP2 number: a Cloud PBX line, a local DID, a toll-free number or a UIFN. It can transfer calls to your extensions, mobiles or a SIP trunk.' },
  ],

  volumeOptions: ['Under 500 calls / month', '500–2,000', '2,000–10,000', '10,000+', 'Not sure'],

  related: [
    { label: 'Cloud PBX', to: '/products/cloud-pbx' },
    { label: 'DID International Numbers', to: '/phone-numbers/did-international' },
    { label: 'Toll-Free Numbers', to: '/phone-numbers/toll-free' },
  ],
};
